import * as React from 'react';
import { TriangleAlert } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { TASK_STATUS_REASON, type Task } from '@klareospace/pharos-lib-types';

import { translateStatusReason } from '&/features/orders/utils';

import {
  hasStatusReasonComment,
  isCancelledTask,
  isFailedTask,
  resolveFailureAttribution,
} from '../utils/task-failure';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TaskFailureCardProps {
  readonly task: Task;
}

interface FailureAttributionRowProps {
  readonly driverName?: string;
  readonly timestamp?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Failure/cancellation details: reason headline, optional driver comment, attribution. */
export function TaskFailureCard({ task }: TaskFailureCardProps): React.JSX.Element | null {
  const { t } = useTranslation('tasks');
  const tOrders = (key: string): string => t(`orders:${key}`);

  const isFailed = isFailedTask(task);
  const isCancelled = isCancelledTask(task);
  if (!isFailed && !isCancelled) return null;

  const fallbackLabel = isFailed ? t('errors.failed') : t('errors.cancelled');
  const reasonLabel = translateStatusReason(tOrders, task.statusReason ?? undefined) ?? fallbackLabel;
  const isOtherReason = task.statusReason === TASK_STATUS_REASON.OTHER;
  const showComment = hasStatusReasonComment(task);
  const { driverName, timestamp } = resolveFailureAttribution(task);
  // task.driver is the assignee, not the cancellation actor — never attribute a cancellation to them.
  const attributedDriverName = isCancelled ? undefined : driverName;

  return (
    <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <TriangleAlert className="w-3.5 h-3.5 text-destructive shrink-0" />
        <span className="text-sm font-medium text-destructive">{reasonLabel}</span>
        {isOtherReason && (
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('failure.driverCommentRequired')}
          </span>
        )}
      </div>
      {showComment && (
        <p className="text-sm text-foreground/80 italic break-words border-l-2 border-destructive/40 pl-2.5">
          “{task.statusReasonMessage?.trim()}”
        </p>
      )}
      <FailureAttributionRow driverName={attributedDriverName} timestamp={timestamp} />
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/** "Reported by {name} · {timestamp}" line — either segment is omitted when its data is missing. */
function FailureAttributionRow({ driverName, timestamp }: FailureAttributionRowProps): React.JSX.Element | null {
  if (driverName === undefined && timestamp === undefined) return null;

  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
      {driverName !== undefined && (
        <Trans
          components={{ driver: <span className="font-medium text-foreground/70" /> }}
          i18nKey="failure.reportedBy"
          ns="tasks"
          values={{ name: driverName }}
        />
      )}
      {driverName !== undefined && timestamp !== undefined && <span className="text-muted-foreground/50">·</span>}
      {timestamp !== undefined && <span>{timestamp}</span>}
    </div>
  );
}
